import React, { useState, useMemo } from 'react';
import { getOperatingDates, getDayName, doesDateMatchFrequency } from '../../../../../utils/frequencyMatcher';
import { formatDisplayDate, formatFrequencyWithUnderscores, getDisplayFrequency, getDisplayDate } from '../../../../../utils/types';
import { useTimezone } from '../../../../../contexts/TimezoneContext';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface FrequencyToolsTabProps {
  frequency: string;
  frequencyMYT?: string;
  startDate: string;
  endDate: string;
  route?: string;
  stdUTC?: string;
}

type SubTabType = 'preview' | 'checker';

export function FrequencyToolsTab({ frequency, frequencyMYT, startDate, endDate, route, stdUTC }: FrequencyToolsTabProps) {
  const { timezone } = useTimezone();
  const displayFreq = getDisplayFrequency(frequency, frequencyMYT, timezone);
  const [activeSubTab, setActiveSubTab] = useState<SubTabType>('preview');
  const [currentPage, setCurrentPage] = useState(1);
  const [checkDate, setCheckDate] = useState('');
  const [checkResult, setCheckResult] = useState<{
    operates: boolean;
    dayName: string;
    reason?: string;
  } | null>(null);

  const ITEMS_PER_PAGE = 10;

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Calculate operating dates for preview using timezone-aware frequency
  const { operatingDates, isPastSchedule } = useMemo(() => {
    if (!displayFreq || !startDate || !endDate) {
      return { operatingDates: [] as Date[], isPastSchedule: false };
    }

    try {
      const scheduleStart = new Date(startDate);
      scheduleStart.setHours(0, 0, 0, 0);

      const scheduleEnd = new Date(endDate);
      scheduleEnd.setHours(0, 0, 0, 0);

      // If schedule is entirely in the past, show dates from the schedule range instead
      const past = scheduleEnd < today;
      const rangeStart = past ? scheduleStart : (today > scheduleStart ? today : scheduleStart);

      const thirtyDaysLater = new Date(rangeStart);
      thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
      const rangeEnd = thirtyDaysLater < scheduleEnd ? thirtyDaysLater : scheduleEnd;

      return {
        operatingDates: getOperatingDates(displayFreq, rangeStart, rangeEnd, scheduleStart, scheduleEnd),
        isPastSchedule: past,
      };
    } catch (error) {
      console.error('Error calculating operating dates:', error);
      return { operatingDates: [] as Date[], isPastSchedule: false };
    }
  }, [displayFreq, startDate, endDate, today]);

  // Pagination calculations
  const totalPages = Math.ceil(operatingDates.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedDates = operatingDates.slice(startIndex, endIndex);

  const freqDisplay = formatFrequencyWithUnderscores(displayFreq);
  const tzLabel = timezone === 'MYT' ? 'MYT' : 'UTC';

  // Reset to page 1 when changing tabs
  const handleSubTabChange = (tab: SubTabType) => {
    setActiveSubTab(tab);
    setCurrentPage(1);
  };

  // Handle date checking
  const handleCheckDate = () => {
    if (!checkDate) {
      setCheckResult(null);
      return;
    }

    try {
      const targetDate = new Date(checkDate);
      targetDate.setHours(0, 0, 0, 0);

      const scheduleStart = new Date(startDate);
      scheduleStart.setHours(0, 0, 0, 0);

      const scheduleEnd = new Date(endDate);
      scheduleEnd.setHours(0, 0, 0, 0);

      const operates = doesDateMatchFrequency(targetDate, displayFreq, scheduleStart, scheduleEnd);
      const dayName = getDayName(targetDate);

      let reason = '';
      if (!operates) {
        if (targetDate < scheduleStart) {
          reason = 'Date is before schedule start date';
        } else if (targetDate > scheduleEnd) {
          reason = 'Date is after schedule end date';
        } else {
          reason = `Flight does not operate on ${dayName}s`;
        }
      }

      setCheckResult({ operates, dayName, reason });
    } catch (error) {
      console.error('Error checking date:', error);
      setCheckResult(null);
    }
  };

  return (
    <div className="flex flex-col w-full" style={{ height: '100%' }}>
      {/* Context Strip */}
      <div className="flex-shrink-0 px-5 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-8">
          {route && (
            <div>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Route</div>
              <div className="text-sm font-semibold text-gray-800">{route}</div>
            </div>
          )}
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Period</div>
            <div className="text-sm font-semibold text-gray-800">
              {formatDisplayDate(getDisplayDate(startDate, stdUTC, timezone))} → {formatDisplayDate(getDisplayDate(endDate, stdUTC, timezone))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Frequency ({tzLabel})</div>
            <div className="text-sm font-mono font-bold text-gray-800 tracking-wider">{freqDisplay}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-primary-700 bg-primary-50 px-2.5 py-1 rounded-md">
            {operatingDates.length} operating days
          </span>
          {isPastSchedule && (
            <span className="text-xs font-semibold text-warning-700 bg-warning-50 px-2.5 py-1 rounded-md">
              Past schedule
            </span>
          )}
        </div>
      </div>

      {/* Sidebar + Content */}
      <div className="flex flex-1 min-h-0">
        {/* Left Sidebar - Navigation */}
        <div className="w-56 bg-gray-50 border-r border-gray-200 flex flex-col shrink-0">
          {/* Navigation Items */}
          <nav className="flex-1 p-3">
            <button
              onClick={() => handleSubTabChange('preview')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-all text-left
                ${activeSubTab === 'preview'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
            >
              <CalendarMonthIcon sx={{ fontSize: 20 }} />
              <div>
                <div className="font-medium text-sm">Operating Dates</div>
                <div className={`text-xs ${activeSubTab === 'preview' ? 'text-primary-100' : 'text-gray-500'}`}>
                  Next 30 days
                </div>
              </div>
            </button>

            <button
              onClick={() => handleSubTabChange('checker')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left
                ${activeSubTab === 'checker'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
            >
              <SearchIcon sx={{ fontSize: 20 }} />
              <div>
                <div className="font-medium text-sm">Date Checker</div>
                <div className={`text-xs ${activeSubTab === 'checker' ? 'text-primary-100' : 'text-gray-500'}`}>
                  Verify specific date
                </div>
              </div>
            </button>
          </nav>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scroll-smooth">
          {/* Sub-Tab: Operating Dates Preview */}
          {activeSubTab === 'preview' && (
            <div className="px-6 py-6">
              {operatingDates.length === 0 ? (
                <div className="px-4 py-8 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <div className="text-yellow-600 mb-2"><WarningAmberIcon sx={{ fontSize: 24 }} /></div>
                  <span className="text-sm text-yellow-800">
                    No operating dates found in the next 30 days.
                  </span>
                </div>
              ) : (
                <>
                  {/* Summary Card */}
                  <div className="mb-4 px-4 py-3 bg-primary-50 border-l-4 border-primary-500 rounded">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-primary-900">
                          Total Operating Days
                        </div>
                        <div className="text-xs text-primary-700 mt-0.5">
                          {isPastSchedule ? 'First 30 days of schedule (past)' : 'In the next 30 days from today'}
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-primary-600">
                        {operatingDates.length}
                      </div>
                    </div>
                  </div>

                  {/* Compact Table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-100 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            #
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Day
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Timeline
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paginatedDates.map((date, index) => {
                          const isPast = date < today;
                          return (
                            <tr
                              key={date.toISOString()}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {startIndex + index + 1}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {date.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {getDayName(date)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                                  Operating
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`text-xs font-medium px-2 py-1 rounded ${
                                  isPast
                                    ? 'bg-gray-100 text-gray-500'
                                    : 'bg-blue-50 text-blue-700'
                                }`}>
                                  {isPast ? 'Past' : 'Upcoming'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                          <span className="font-medium">{Math.min(endIndex, operatingDates.length)}</span> of{' '}
                          <span className="font-medium">{operatingDates.length}</span> dates
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                          >
                            <ChevronLeftIcon sx={{ fontSize: 18 }} />
                            Previous
                          </button>

                          <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                                  ${currentPage === page
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                  }`}
                              >
                                {page}
                              </button>
                            ))}
                          </div>

                          <button
                            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                          >
                            Next
                            <ChevronRightIcon sx={{ fontSize: 18 }} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Sub-Tab: Date Checker */}
          {activeSubTab === 'checker' && (
            <div className="px-6 py-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Check Specific Date</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Enter a specific date to check if this flight operates on that day.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Date
                    </label>
                    <input
                      type="date"
                      value={checkDate}
                      onChange={(e) => {
                        setCheckDate(e.target.value);
                        setCheckResult(null);
                      }}
                      className="w-full px-4 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handleCheckDate}
                      disabled={!checkDate}
                      className="w-full md:w-auto px-6 py-2.5 text-sm font-medium rounded-lg transition-colors border-none disabled:cursor-not-allowed bg-primary-500 text-white hover:bg-primary-700 disabled:bg-gray-400"
                    >
                      Check Date
                    </button>
                  </div>
                </div>

                {checkResult && (
                  <div className="mt-4">
                    {checkResult.operates ? (
                      <div className="px-4 py-4 bg-green-50 border-2 border-green-400 rounded-lg">
                        <div className="flex items-start gap-3">
                          <CheckCircleIcon className="text-green-600 mt-0.5" sx={{ fontSize: 28 }} />
                          <div className="flex-1">
                            <div className="text-base font-semibold text-green-800 mb-2">
                              Flight operates on this date
                            </div>
                            <div className="text-sm text-green-700">
                              {new Date(checkDate).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="px-4 py-4 bg-red-50 border-2 border-red-400 rounded-lg">
                        <div className="flex items-start gap-3">
                          <CancelIcon className="text-red-600" sx={{ fontSize: 28 }} />
                          <div className="flex-1">
                            <div className="text-base font-semibold text-red-800 mb-2">
                              Flight does NOT operate on this date
                            </div>
                            <div className="text-sm text-red-700 mb-3">
                              {new Date(checkDate).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </div>
                            {checkResult.reason && (
                              <div className="text-xs text-red-600 bg-red-100 px-3 py-2 rounded border border-red-300">
                                <strong>Reason:</strong> {checkResult.reason}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
